import {
  format,
  formatDistanceToNow,
  isValid,
  parse,
  parseISO,
} from "date-fns";

const MIN_YEAR = 2020;
const MAX_YEAR = 2100;

const FLEXIBLE_DATE_FORMATS = [
  "dMMMyyyy",
  "ddMMMyyyy",
  "dMMMyy",
  "ddMMMyy",
  "d MMM yyyy",
  "dd MMM yyyy",
  "d-MMM-yyyy",
  "dd-MMM-yyyy",
  "d MMMM yyyy",
  "dd MMMM yyyy",
  "d-MMMM-yyyy",
  "dd-MMMM-yyyy",
] as const;

export const isExcelDateSerial = (value: any): boolean => {
  if (typeof value !== "number") return false;
  // Only accept Excel date serials for years 2020-2100 (43831-73050)
  return value >= 43831 && value <= 73050;
};

export const excelSerialToDate = (serial: number): Date => {
  // Excel incorrectly treats 1900 as a leap year
  const excelEpoch = new Date(1900, 0, 1);
  const daysOffset = serial - 2; // -1 for 1-based indexing, -1 for Excel leap year bug
  return new Date(excelEpoch.getTime() + daysOffset * 24 * 60 * 60 * 1000);
};

const normalizeMonthCasing = (value: string): string => {
  if (!value) {
    return value;
  }

  let normalized = "";
  let index = 0;

  while (index < value.length) {
    const char = value[index];

    if (/[a-zA-Z]/.test(char)) {
      let segment = "";
      while (index < value.length && /[a-zA-Z]/.test(value[index])) {
        segment += value[index];
        index += 1;
      }
      const lowerSegment = segment.toLowerCase();
      normalized += upperFirst(lowerSegment);
      continue;
    }

    normalized += char;
    index += 1;
  }

  return normalized;
};

const upperFirst = (input: string): string =>
  input.length === 0 ? input : input[0].toUpperCase() + input.slice(1);

const parseUsingFlexibleFormats = (value: string): Date | null => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();
  if (trimmedValue.length < 4) {
    return null;
  }

  const normalizedValue = normalizeMonthCasing(trimmedValue);

  for (const pattern of FLEXIBLE_DATE_FORMATS) {
    const parsed = parse(normalizedValue, pattern, new Date());
    if (
      isValid(parsed) &&
      parsed.getFullYear() >= MIN_YEAR &&
      parsed.getFullYear() < MAX_YEAR
    ) {
      return parsed;
    }
  }

  return null;
};

// Detect date format pattern from column data by analyzing surrounding dates
// Only called when we encounter an ambiguous date pattern
export const detectDateFormatFromColumn = (
  columnData: any[],
  currentIndex: number,
  lookAround: number = 1
): "MM/DD/YYYY" | "DD/MM/YYYY" | null => {
  if (!columnData || columnData.length === 0) {
    return null;
  }

  const totalRows = columnData.length;
  let startIndex: number;
  let endIndex: number;

  // Determine look-around range based on position
  if (currentIndex === 0) {
    // First row: check next row only
    startIndex = 0;
    endIndex = Math.min(totalRows, currentIndex + lookAround + 1);
  } else if (currentIndex === totalRows - 1) {
    // Last row: check previous row only
    startIndex = Math.max(0, currentIndex - lookAround);
    endIndex = totalRows;
  } else {
    // Middle rows: check both above and below (1 row each)
    startIndex = Math.max(0, currentIndex - lookAround);
    endIndex = Math.min(totalRows, currentIndex + lookAround + 1);
  }

  const surroundingData = columnData.slice(startIndex, endIndex);

  let mmddCount = 0;
  let ddmmCount = 0;
  let totalAnalyzed = 0;

  // Analyze surrounding dates, excluding the current cell
  for (let i = 0; i < surroundingData.length; i++) {
    const dataIndex = startIndex + i;
    // Skip the current cell - we're trying to parse it
    if (dataIndex === currentIndex) {
      continue;
    }

    const cellValue = surroundingData[i];
    if (!cellValue || typeof cellValue !== "string") {
      continue;
    }

    const trimmedValue = cellValue.trim();
    // Check if it matches MM/DD/YYYY or DD/MM/YYYY pattern
    const ambiguousPattern = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
    const match = trimmedValue.match(ambiguousPattern);

    if (!match) {
      continue;
    }

    const [, part1, part2, year] = match;
    const num1 = parseInt(part1, 10);
    const num2 = parseInt(part2, 10);
    const yearNum = parseInt(year, 10);

    // Skip if year is out of range
    if (yearNum < MIN_YEAR || yearNum >= MAX_YEAR) {
      continue;
    }

    // If unambiguous, we can determine the format
    if (num1 > 12) {
      // First part > 12, must be DD/MM/YYYY
      ddmmCount++;
      totalAnalyzed++;
    } else if (num2 > 12) {
      // Second part > 12, must be MM/DD/YYYY
      mmddCount++;
      totalAnalyzed++;
    } else {
      // Ambiguous date - try both formats and see which makes more sense
      const separator = trimmedValue.includes("/") ? "/" : "-";
      const format1 = separator === "/" ? "MM/dd/yyyy" : "MM-dd-yyyy";
      const format2 = separator === "/" ? "dd/MM/yyyy" : "dd-MM-yyyy";

      const parsed1 = parse(trimmedValue, format1, new Date());
      const parsed2 = parse(trimmedValue, format2, new Date());

      const valid1 =
        isValid(parsed1) &&
        parsed1.getFullYear() === yearNum &&
        parsed1.getDate() <= 31 &&
        parsed1.getMonth() >= 0 &&
        parsed1.getMonth() <= 11;

      const valid2 =
        isValid(parsed2) &&
        parsed2.getFullYear() === yearNum &&
        parsed2.getDate() <= 31 &&
        parsed2.getMonth() >= 0 &&
        parsed2.getMonth() <= 11;

      // If only one is valid, count it
      if (valid1 && !valid2) {
        mmddCount++;
        totalAnalyzed++;
      } else if (valid2 && !valid1) {
        ddmmCount++;
        totalAnalyzed++;
      }
      // If both are valid, we can't determine from this one - skip it
    }
  }

  // Need at least 1 sample to make a determination (but prefer more for accuracy)
  if (totalAnalyzed >= 1) {
    // Determine format based on majority
    if (mmddCount > ddmmCount) {
      return "MM/DD/YYYY";
    } else if (ddmmCount > mmddCount) {
      return "DD/MM/YYYY";
    }
    // If equal, continue to sequence analysis
  }

  // If no unambiguous dates found, try sequence analysis
  return detectDateFormatBySequence(columnData, currentIndex, lookAround);
};

// Analyze date sequences to detect format when all dates are ambiguous
const detectDateFormatBySequence = (
  columnData: any[],
  currentIndex: number,
  lookAround: number = 1
): "MM/DD/YYYY" | "DD/MM/YYYY" | null => {
  if (!columnData || columnData.length === 0) {
    return null;
  }

  const totalRows = columnData.length;
  let startIndex: number;
  let endIndex: number;

  // Determine look-around range based on position
  if (currentIndex === 0) {
    startIndex = 0;
    endIndex = Math.min(totalRows, currentIndex + lookAround + 3); // Check more rows for sequence
  } else if (currentIndex === totalRows - 1) {
    startIndex = Math.max(0, currentIndex - lookAround - 2);
    endIndex = totalRows;
  } else {
    startIndex = Math.max(0, currentIndex - lookAround - 2);
    endIndex = Math.min(totalRows, currentIndex + lookAround + 3);
  }

  // Collect all date strings in the range
  const dateStrings: string[] = [];
  for (let i = startIndex; i < endIndex; i++) {
    const cellValue = columnData[i];
    if (cellValue && typeof cellValue === "string") {
      const trimmedValue = cellValue.trim();
      const ambiguousPattern = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
      if (ambiguousPattern.test(trimmedValue)) {
        dateStrings.push(trimmedValue);
      }
    }
  }

  // Need at least 3 dates to analyze sequence
  if (dateStrings.length < 3) {
    return null;
  }

  const separator = dateStrings[0].includes("/") ? "/" : "-";
  const format1 = separator === "/" ? "MM/dd/yyyy" : "MM-dd-yyyy";
  const format2 = separator === "/" ? "dd/MM/yyyy" : "dd-MM-yyyy";

  // Parse all dates in both formats
  const parsedMMDD: Date[] = [];
  const parsedDDMM: Date[] = [];

  for (const dateStr of dateStrings) {
    const parsed1 = parse(dateStr, format1, new Date());
    const parsed2 = parse(dateStr, format2, new Date());

    if (isValid(parsed1)) {
      parsedMMDD.push(parsed1);
    }
    if (isValid(parsed2)) {
      parsedDDMM.push(parsed2);
    }
  }

  // Check which format produces a sequential pattern
  const mmddSequential = isSequential(parsedMMDD);
  const ddmmSequential = isSequential(parsedDDMM);

  // Prefer format that creates a sequential pattern
  if (ddmmSequential && !mmddSequential) {
    return "DD/MM/YYYY";
  } else if (mmddSequential && !ddmmSequential) {
    return "MM/DD/YYYY";
  } else if (ddmmSequential && mmddSequential) {
    // Both are sequential - prefer the one with more consistent pattern
    // (e.g., sequential days in same month vs sequential months)
    const ddmmConsistency = getSequenceConsistency(parsedDDMM);
    const mmddConsistency = getSequenceConsistency(parsedMMDD);

    if (ddmmConsistency > mmddConsistency) {
      return "DD/MM/YYYY";
    } else if (mmddConsistency > ddmmConsistency) {
      return "MM/DD/YYYY";
    }
    // If both equally consistent, default to MM/DD/YYYY
    return "MM/DD/YYYY";
  }

  return null;
};

// Check if dates form a sequential pattern (increasing order)
const isSequential = (dates: Date[]): boolean => {
  if (dates.length < 2) {
    return false;
  }

  // Sort dates to check if they're in order
  const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());

  // Check if dates are in increasing order (allowing for small gaps)
  for (let i = 1; i < sortedDates.length; i++) {
    const prev = sortedDates[i - 1];
    const curr = sortedDates[i];
    const diffDays = Math.floor(
      (curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Allow gaps up to 7 days (to handle weekends, etc.)
    if (diffDays < 0 || diffDays > 7) {
      return false;
    }
  }

  return true;
};

// Get consistency score for a sequence (higher = more consistent)
// Prefers sequences where dates are in same month (sequential days)
const getSequenceConsistency = (dates: Date[]): number => {
  if (dates.length < 2) {
    return 0;
  }

  const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
  let sameMonthCount = 0;
  let sequentialDaysCount = 0;

  for (let i = 1; i < sortedDates.length; i++) {
    const prev = sortedDates[i - 1];
    const curr = sortedDates[i];

    // Check if same month
    if (
      prev.getMonth() === curr.getMonth() &&
      prev.getFullYear() === curr.getFullYear()
    ) {
      sameMonthCount++;
    }

    // Check if sequential days
    const diffDays = Math.floor(
      (curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (diffDays === 1) {
      sequentialDaysCount++;
    }
  }

  // Weight sequential days more heavily
  return sameMonthCount * 2 + sequentialDaysCount * 3;
};

// Parse ambiguous dates using detected format from column context
const parseAmbiguousDateWithContext = (
  value: string,
  detectedFormat: "MM/DD/YYYY" | "DD/MM/YYYY" | null
): Date | null => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();
  const ambiguousPattern = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
  const match = trimmedValue.match(ambiguousPattern);

  if (!match) {
    return null;
  }

  const [, , , year] = match;
  const yearNum = parseInt(year, 10);

  if (yearNum < MIN_YEAR || yearNum >= MAX_YEAR) {
    return null;
  }

  const separator = trimmedValue.includes("/") ? "/" : "-";

  // If we have detected format, use it
  if (detectedFormat === "MM/DD/YYYY") {
    const format = separator === "/" ? "MM/dd/yyyy" : "MM-dd-yyyy";
    const parsed = parse(trimmedValue, format, new Date());
    if (
      isValid(parsed) &&
      parsed.getFullYear() === yearNum &&
      parsed.getFullYear() >= MIN_YEAR &&
      parsed.getFullYear() < MAX_YEAR
    ) {
      return parsed;
    }
  } else if (detectedFormat === "DD/MM/YYYY") {
    const format = separator === "/" ? "dd/MM/yyyy" : "dd-MM-yyyy";
    const parsed = parse(trimmedValue, format, new Date());
    if (
      isValid(parsed) &&
      parsed.getFullYear() === yearNum &&
      parsed.getFullYear() >= MIN_YEAR &&
      parsed.getFullYear() < MAX_YEAR
    ) {
      return parsed;
    }
  }

  // If no detected format or parsing failed, try both
  const format1 = separator === "/" ? "MM/dd/yyyy" : "MM-dd-yyyy";
  const format2 = separator === "/" ? "dd/MM/yyyy" : "dd-MM-yyyy";

  const parsed1 = parse(trimmedValue, format1, new Date());
  const parsed2 = parse(trimmedValue, format2, new Date());

  const valid1 =
    isValid(parsed1) &&
    parsed1.getFullYear() === yearNum &&
    parsed1.getFullYear() >= MIN_YEAR &&
    parsed1.getFullYear() < MAX_YEAR;

  const valid2 =
    isValid(parsed2) &&
    parsed2.getFullYear() === yearNum &&
    parsed2.getFullYear() >= MIN_YEAR &&
    parsed2.getFullYear() < MAX_YEAR;

  // Prefer detected format if available, otherwise default to MM/DD/YYYY
  if (detectedFormat === "MM/DD/YYYY" && valid1) {
    return parsed1;
  }
  if (detectedFormat === "DD/MM/YYYY" && valid2) {
    return parsed2;
  }

  // Fallback: try both
  if (valid1) {
    return parsed1;
  }
  if (valid2) {
    return parsed2;
  }

  return null;
};

export const isDateValue = (value: any): boolean => {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  if (value instanceof Date) {
    const year = value.getFullYear();
    return !isNaN(value.getTime()) && year >= MIN_YEAR && year < MAX_YEAR;
  }

  // Check for Excel date serial (as number)
  if (typeof value === "number" && isExcelDateSerial(value)) {
    return true;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (trimmedValue.length < 4 || /^[a-zA-Z]+$/.test(trimmedValue)) {
      return false;
    }

    // Check for Excel date serial as string (e.g., "45302")
    if (/^\d+$/.test(trimmedValue)) {
      const numValue = parseInt(trimmedValue, 10);
      if (isExcelDateSerial(numValue)) {
        return true;
      }
      return false; // Pure numeric string that's not a valid Excel serial
    }

    // Try flexible formats (dates with month names - unambiguous)
    const parsedFlexibleDate = parseUsingFlexibleFormats(trimmedValue);
    if (parsedFlexibleDate) {
      return true;
    }

    // Check for ISO format YYYY-MM-DD (unambiguous)
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
      const parsed = parseISO(trimmedValue);
      if (isValid(parsed)) {
        const year = parsed.getFullYear();
        return year >= MIN_YEAR && year < MAX_YEAR;
      }
    }

    // Check for DD-MM-YYYY or MM-DD-YYYY patterns (need special handling)
    const ambiguousPattern = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
    const match = trimmedValue.match(ambiguousPattern);
    if (match) {
      const [, part1, part2, year] = match;
      const num1 = parseInt(part1, 10);
      const num2 = parseInt(part2, 10);
      const yearNum = parseInt(year, 10);

      // Year must be in valid range
      if (yearNum < MIN_YEAR || yearNum >= MAX_YEAR) {
        return false;
      }

      // Validate that day/month parts are reasonable
      // If num1 > 12, it must be day (DD-MM-YYYY)
      // If num2 > 12, it must be day (MM-DD-YYYY)
      // If both <= 12, it's ambiguous but still a valid date format
      if (num1 > 31 || num2 > 31) {
        return false; // Invalid day value
      }
      if (num1 > 12 && num2 > 12) {
        return false; // Both can't be days
      }

      // At least one interpretation is valid
      return true;
    }

    // Check for YYYY/MM/DD format
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(trimmedValue)) {
      const dateValue = new Date(trimmedValue);
      if (!isNaN(dateValue.getTime())) {
        const year = dateValue.getFullYear();
        return year >= MIN_YEAR && year < MAX_YEAR;
      }
    }

    // Try parsing with JavaScript Date for other formats (e.g., "05-Aug-2025", "Jan 15 2024")
    const dateValue = new Date(trimmedValue);
    if (
      !isNaN(dateValue.getTime()) &&
      dateValue.getFullYear() >= MIN_YEAR &&
      dateValue.getFullYear() < MAX_YEAR
    ) {
      // Additional validation: ensure the string contains date-like content
      // Must have numbers and either separators or month names
      if (
        /\d/.test(trimmedValue) &&
        (/[-/\s]/.test(trimmedValue) || /[A-Za-z]{3}/.test(trimmedValue))
      ) {
        return true;
      }
    }

    return false;
  }

  return false;
};

export const formatDateValue = (date: Date): string => {
  try {
    if (isValid(date)) {
      return format(date, "yyyy-MM-dd");
    }
    // Fallback to ISO string method if date-fns format fails
    return date.toISOString().split("T")[0];
  } catch (error) {
    return date.toISOString().split("T")[0];
  }
};

export const parseValueAsDate = (
  value: any,
  columnData?: any[],
  currentIndex?: number
): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  // If it's already a Date object
  if (value instanceof Date) {
    // Validate year bounds to prevent corrupted dates
    if (
      isValid(value) &&
      value.getFullYear() >= MIN_YEAR &&
      value.getFullYear() < MAX_YEAR
    ) {
      return formatDateValue(value);
    }
    return null;
  }

  // If it's a number, check if it could be an Excel date serial
  if (typeof value === "number" && isExcelDateSerial(value)) {
    const convertedDate = excelSerialToDate(value);
    return isValid(convertedDate) ? formatDateValue(convertedDate) : null;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    // Check for Excel date serial as string (e.g., "45302")
    if (/^\d+$/.test(trimmedValue)) {
      const numValue = parseInt(trimmedValue, 10);
      if (isExcelDateSerial(numValue)) {
        const convertedDate = excelSerialToDate(numValue);
        return isValid(convertedDate) ? formatDateValue(convertedDate) : null;
      }
      // Pure numeric string that's not a valid Excel serial - not a date
      return null;
    }

    // Try flexible formats first (dates with month names - unambiguous)
    const parsedFlexibleDate = parseUsingFlexibleFormats(trimmedValue);
    if (parsedFlexibleDate) {
      return formatDateValue(parsedFlexibleDate);
    }

    // Try ISO format (YYYY-MM-DD - unambiguous)
    const parsedDate = parseISO(trimmedValue);
    if (isValid(parsedDate)) {
      const year = parsedDate.getFullYear();
      if (year >= MIN_YEAR && year < MAX_YEAR) {
        return formatDateValue(parsedDate);
      }
    }

    // Check for ambiguous dates (MM/DD/YYYY vs DD/MM/YYYY)
    const ambiguousPattern = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
    const match = trimmedValue.match(ambiguousPattern);
    if (match) {
      const [, part1, part2, year] = match;
      const num1 = parseInt(part1, 10);
      const num2 = parseInt(part2, 10);
      const yearNum = parseInt(year, 10);

      // Year must be in valid range
      if (yearNum < MIN_YEAR || yearNum >= MAX_YEAR) {
        return null;
      }

      // Only check format detection if date is actually ambiguous (both parts <= 12)
      const isAmbiguous = num1 <= 12 && num2 <= 12;

      if (isAmbiguous) {
        // Detect format from column context if available
        let detectedFormat: "MM/DD/YYYY" | "DD/MM/YYYY" | null = null;
        if (columnData && typeof currentIndex === "number") {
          detectedFormat = detectDateFormatFromColumn(columnData, currentIndex);
        }

        const parsedAmbiguous = parseAmbiguousDateWithContext(
          trimmedValue,
          detectedFormat
        );
        if (parsedAmbiguous) {
          return formatDateValue(parsedAmbiguous);
        }
      } else {
        // Unambiguous date - parse directly without context detection
        const separator = trimmedValue.includes("/") ? "/" : "-";
        let parsed: Date | null = null;

        if (num1 > 12) {
          // First part > 12, must be DD/MM/YYYY
          const dateFormat = separator === "/" ? "dd/MM/yyyy" : "dd-MM-yyyy";
          parsed = parse(trimmedValue, dateFormat, new Date());
        } else if (num2 > 12) {
          // Second part > 12, must be MM/DD/YYYY
          const dateFormat = separator === "/" ? "MM/dd/yyyy" : "MM-dd-yyyy";
          parsed = parse(trimmedValue, dateFormat, new Date());
        }

        if (parsed && isValid(parsed) && parsed.getFullYear() === yearNum) {
          return formatDateValue(parsed);
        }
      }

      // If we matched the pattern but couldn't parse, return null
      return null;
    }

    // Try parsing with JavaScript Date (handles formats like "05-Aug-2025", "Jan 15 2024", etc.)
    const dateValue = new Date(trimmedValue);
    if (
      !isNaN(dateValue.getTime()) &&
      dateValue.getFullYear() >= MIN_YEAR &&
      dateValue.getFullYear() < MAX_YEAR
    ) {
      // Additional validation: ensure the string contains date-like content
      if (
        /\d/.test(trimmedValue) &&
        (/[-/\s]/.test(trimmedValue) || /[A-Za-z]{3}/.test(trimmedValue))
      ) {
        return formatDateValue(dateValue);
      }
    }

    // No valid date format found
    return null;
  }

  return null;
};

export const parseCellValueByType = (
  value: any,
  columnType: "STRING" | "INTEGER" | "DECIMAL" | "DATE"
): string | number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  switch (columnType) {
    case "INTEGER":
    case "DECIMAL":
      if (typeof value === "number") {
        return value;
      } else {
        const numValue = parseFloat(String(value));
        return isNaN(numValue) ? null : numValue;
      }

    case "DATE":
      const dateParsedValue = parseValueAsDate(value);
      return dateParsedValue;

    case "STRING":
      return String(value);

    default:
      return String(value); // Fallback for safety
  }
};

/**
 * Gets today's date (start of day) in the specified IANA timezone.
 * Uses Intl.DateTimeFormat to determine the current date in the target timezone,
 * then returns a local Date object representing midnight of that date.
 * Falls back to the local system date if the timezone is invalid.
 *
 * @param timezone - IANA timezone string (e.g., "America/New_York", "Asia/Kolkata", "UTC")
 * @returns A Date object set to midnight of today in the given timezone
 */
export const getTodayInTimezone = (timezone: string): Date => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    // en-CA locale formats as YYYY-MM-DD
    const dateStr = formatter.format(now);
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  } catch {
    // Fallback to local today if timezone string is invalid
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }
};

// This threshold corresponds to 1970-04-26 17:46:40 UTC in milliseconds.
// It's used to guess if a timestamp is in seconds or milliseconds.
// This heuristic assumes that any millisecond timestamp we receive will be after this date,
// which is a safe assumption for "last updated"/notification timestamps in this application.
// Ideally, the API should enforce a consistent timestamp unit (milliseconds).
const SECONDS_VS_MILLISECONDS_THRESHOLD = 10_000_000_000;

export const epochToDate = (epoch: number): Date => {
  const epochMs =
    epoch < SECONDS_VS_MILLISECONDS_THRESHOLD ? epoch * 1000 : epoch;
  return new Date(epochMs);
};

/**
 * Formats an epoch timestamp to a human-readable relative time string
 * Automatically detects if the timestamp is in seconds or milliseconds
 * @param epoch - Epoch timestamp (in seconds or milliseconds)
 * @returns A string like "2 hours ago", "5 minutes ago", etc.
 */
export const formatTimeAgo = (epoch: number): string => {
  try {
    const date = epochToDate(epoch);
    if (isValid(date)) {
      return formatDistanceToNow(date, { addSuffix: true });
    }
    return "";
  } catch {
    return "";
  }
};

/**
 * Compact variant of formatTimeAgo ("5m ago"/"3h ago"/"3d ago" instead of
 * date-fns's prose "5 minutes ago"), falling back to an absolute date once
 * the distance exceeds a week. Used where space is tight (notification lists).
 * @param epoch - Epoch timestamp (in seconds or milliseconds)
 */
export const formatCompactTimeAgo = (epoch: number): string => {
  try {
    const date = epochToDate(epoch);
    if (!isValid(date)) return "";
    const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return format(date, "MMM d, yyyy");
  } catch {
    return "";
  }
};
